import { deviceManager } from './deviceManager';

/**
 * Global Audio Manager for Persistent Voice Processing and Background Operation
 * Handles microphone permissions, audio context, and persistent voice data
 * Updated to work with the new DeviceManager for device management
 */

class GlobalAudioManager {
  constructor() {
    this.audioContext = null;
    this.microphoneStream = null;
    this.isInitialized = false;
    this.isInitializing = false;
    this.isPersistentMode = false;
    this.setupCount = 0;
    this.permissions = {
      microphone: null,
      timestamp: null
    };
    this.initPromise = null;
    
    // Device management is now handled by DeviceManager
    this.deviceManager = deviceManager;
    
    // Persistent voice processing state
    this.analyser = null;
    this.mediaRecorder = null;
    this.processingInterval = null;
    this.eventListeners = new Map();
    this.voiceData = {
      frequency: new Float32Array(128),
      waveform: new Float32Array(1024),
      volume: 0,
      isActive: false
    };
    
    // Background processing configuration
    this.backgroundConfig = {
      enabled: false,
      updateInterval: 50, // 20fps for smooth visualization
      volumeThreshold: 0.01,
      activityTimeout: 500 // ms
    };
    
    // Bind methods for proper event listener removal
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.processAudioData = this.processAudioData.bind(this);
    this.handleDeviceSwitch = this.handleDeviceSwitch.bind(this);
    this.handleDeviceChange = this.handleDeviceChange.bind(this);
    
    // Setup global listeners after construction
    setTimeout(() => {
      this.setupGlobalListeners();
      // Device change detection is now handled by DeviceManager
      this.setupDeviceManagerListeners();
    }, 0);
  }

  setupGlobalListeners() {
    // Handle page visibility changes (but don't stop voice processing)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Handle window focus/blur - continue processing during app switching
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('focus', this.handleFocus);
    
    // Handle user interaction for audio context
    const enableAudioContext = () => {
      if (!this.audioContext) {
        this.createAudioContext();
      }
      // Auto-start persistent mode if configured
      if (this.backgroundConfig.enabled && !this.isPersistentMode) {
        this.startPersistentProcessing();
      }
      document.removeEventListener('click', enableAudioContext);
      document.removeEventListener('keydown', enableAudioContext);
    };
    
    document.addEventListener('click', enableAudioContext, { once: true });
    document.addEventListener('keydown', enableAudioContext, { once: true });
    
    // Listen for Electron window state changes
    if (window.electronAPI) {
      window.electronAPI.onWindowStateChange?.((state) => {
        this.emit('windowStateChange', state);
        // Continue voice processing regardless of window state
      });
    }
  }
  
  setupDeviceManagerListeners() {
    // Listen for device changes from DeviceManager
    this.deviceManager.on('inputDeviceChanged', this.handleDeviceSwitch);
    this.deviceManager.on('deviceChange', this.handleDeviceChange);
  }
  
  handleDeviceSwitch(deviceInfo) {
    console.log('🔄 Device switched via DeviceManager:', deviceInfo);
    
    // Update our stream reference
    this.microphoneStream = this.deviceManager.getAudioStream();
    
    // Reconnect audio processing if needed
    if (this.isPersistentMode) {
      this.reconnectAudioProcessing();
    }
    
    // Emit event for other components
    this.emit('deviceSwitched', {
      oldDevice: this.currentDeviceId || 'unknown',
      newDevice: deviceInfo.deviceId,
      timestamp: Date.now()
    });
  }
  
  async handleDeviceChange() {
    const now = Date.now();
    
    // Smart debouncing - allow immediate processing for disconnections
    const timeSinceLastChange = now - this.lastDeviceChangeTime;
    const isCurrentStreamInactive = await this.isCurrentStreamHealthy() === false;
    
    // Skip debounce if current stream is broken (device disconnected)
    if (!isCurrentStreamInactive && timeSinceLastChange < this.deviceChangeDebounceMs) {
      console.log(`🕐 Debouncing device change (${timeSinceLastChange}ms < ${this.deviceChangeDebounceMs}ms)`);
      return;
    }
    
    if (this.isHandlingDeviceChange) {
      console.log('🔄 Device change already in progress...');
      // If we're handling a change but current stream is broken, force a retry
      if (isCurrentStreamInactive) {
        console.log('🚨 Current stream broken during device change - forcing retry');
        this.isHandlingDeviceChange = false;
      } else {
        return;
      }
    }
    
    this.lastDeviceChangeTime = now;
    this.isHandlingDeviceChange = true;
    
    try {
      console.log('🔧 Device change detected, analyzing available devices through DeviceManager...');
      
      // Get current system devices from DeviceManager
      const audioInputs = this.deviceManager.getAvailableInputDevices();
      
      // Log all available devices for debugging
      console.log('🔍 Available audio input devices:', audioInputs.map(d => ({ id: d.deviceId, label: d.label })));
      
      // Get current device info
      let currentDeviceLabel = 'Unknown';
      let currentDeviceId = 'unknown';
      if (this.microphoneStream && this.microphoneStream.active) {
        const currentTrack = this.microphoneStream.getAudioTracks()[0];
        currentDeviceLabel = currentTrack?.label || 'Unknown';
        const settings = currentTrack?.getSettings();
        currentDeviceId = settings?.deviceId || 'unknown';
      }
      
      console.log(`🎤 Current device: "${currentDeviceLabel}" (ID: ${currentDeviceId})`);
      
      // Smart device prioritization - Look for preferred devices
      let bestDevice = null;
      let switchReason = '';
      
      // Priority 1: Headset/Earphone microphones (highest priority)
      const headsetMics = audioInputs.filter(device => {
        const label = device.label.toLowerCase();
        return label.includes('headset') || 
               label.includes('headphone') || 
               label.includes('earphone') ||
               (label.includes('microphone') && 
                !label.includes('array') && 
                !label.includes('webcam') &&
                device.deviceId !== 'default');
      });
      
      if (headsetMics.length > 0) {
        // Check if we're already using a headset mic
        const currentIsHeadset = headsetMics.some(device => 
          device.label === currentDeviceLabel || device.deviceId === currentDeviceId
        );
        
        if (!currentIsHeadset) {
          bestDevice = headsetMics[0];
          switchReason = 'Detected preferred headset/earphone microphone';
          console.log(`🎧 ${switchReason}: ${bestDevice.label}`);
        }
      }
      
      // Priority 2: If no headset found, check for any new non-array microphones
      if (!bestDevice) {
        const nonArrayMics = audioInputs.filter(device => {
          const label = device.label.toLowerCase();
          return device.deviceId !== 'default' &&
                 device.deviceId !== 'communications' &&
                 !label.includes('array') &&
                 device.label !== currentDeviceLabel;
        });
        
        if (nonArrayMics.length > 0) {
          bestDevice = nonArrayMics[0];
          switchReason = 'Detected new microphone device';
          console.log(`🎤 ${switchReason}: ${bestDevice.label}`);
        }
      }
      
      // Priority 3: Handle disconnections and fallbacks
      if (!bestDevice) {
        // Check if current device is no longer available (disconnection scenario)
        const currentDeviceStillExists = audioInputs.some(device => 
          device.label === currentDeviceLabel || device.deviceId === currentDeviceId
        );
        
        if (!currentDeviceStillExists && currentDeviceLabel !== 'Unknown') {
          console.log(`🔌 Current device "${currentDeviceLabel}" disconnected - forcing fallback`);
          const fallbackSuccess = await this.handleDeviceDisconnectionFallback();
          if (fallbackSuccess) {
            console.log('✅ Successfully switched to fallback device');
          }
        } else if (this.currentDeviceId === 'default') {
          console.log('🔄 Force-checking system default for changes...');
          // Use DeviceManager to refresh and check for changes
          await this.deviceManager.refreshDeviceList();
          const switchOccurred = await this.switchToSystemDefaultWithRetry();
          if (switchOccurred) {
            console.log('✅ System default device changed automatically');
          }
        }
      }
      
      // Execute the switch if we found a better device
      if (bestDevice) {
        console.log(`🔄 Switching from "${currentDeviceLabel}" to "${bestDevice.label}"`);
        // Use DeviceManager to switch devices
        await this.deviceManager.setInputDevice(bestDevice.deviceId);
        this.microphoneStream = this.deviceManager.getAudioStream();
        
        console.log(`✅ Successfully switched to: ${bestDevice.label}`);
        
        // Reconnect audio processing
        if (this.isPersistentMode && this.analyser) {
          await this.reconnectAudioProcessing();
        }
        
        // Emit device switch event
        this.emit('deviceSwitched', {
          oldDevice: currentDeviceLabel,
          newDevice: bestDevice.label,
          oldDeviceId: currentDeviceId,
          newDeviceId: bestDevice.deviceId,
          timestamp: now
        });
      } else {
        console.log('📱 No better device found, staying with current device');
      }
      
      // Notify all listeners about device change
      this.emit('deviceChange', {
        devices: audioInputs,
        timestamp: now
      });
      
    } catch (error) {
      console.error('❌ Error handling device change:', error);
    } finally {
      this.isHandlingDeviceChange = false;
    }
  }
  
  async reconnectAudioProcessing() {
    if (!this.analyser || !this.audioContext || !this.microphoneStream) {
      return;
    }
    
    try {
      // Disconnect old source
      // Note: We can't directly disconnect, but creating new source will replace it
      
      // Create new source with new stream
      const source = this.audioContext.createMediaStreamSource(this.microphoneStream);
      source.connect(this.analyser);
      
      console.log('🔄 Audio processing reconnected to new device');
    } catch (error) {
      console.error('❌ Failed to reconnect audio processing:', error);
    }
  }
  
  handleBlur() {
    // App lost focus (Alt+Tab, clicked other app) - continue processing
    this.emit('windowFocus', false);
    // DON'T stop processing - just reduce frequency for efficiency during app switching
    if (this.processingInterval && this.isPersistentMode) {
      clearInterval(this.processingInterval);
      this.processingInterval = null; // ⚠️ MEMORY LEAK FIX: Null the reference
      // Slightly reduce frequency during app switching to maintain performance
      this.processingInterval = setInterval(() => {
        if (this.isPersistentMode && this.analyser) {
          this.processAudioData();
        }
      }, this.backgroundConfig.updateInterval * 1.2);
    }
    // Emit specific event for Alt+Tab scenarios
    this.emit('appSwitched', { focused: false, timestamp: Date.now() });
  }
  
  handleFocus() {
    // App gained focus - resume normal processing
    this.emit('windowFocus', true);
    // Resume normal frequency immediately when app regains focus
    if (this.processingInterval && this.isPersistentMode) {
      clearInterval(this.processingInterval);
      this.processingInterval = null; // ⚠️ MEMORY LEAK FIX: Null the reference
      this.processingInterval = setInterval(() => {
        if (this.isPersistentMode && this.analyser) {
          this.processAudioData();
        }
      }, this.backgroundConfig.updateInterval);
    }
    // Resume audio context if suspended
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    // Emit specific event for Alt+Tab scenarios
    this.emit('appSwitched', { focused: true, timestamp: Date.now() });
  }

  // Event emitter methods for persistent communication
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);
  }
  
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback);
    }
  }
  
  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }
  
  // ============================================================================
  // STREAM HEALTH MONITORING AND VALIDATION
  // ============================================================================
  
  async isCurrentStreamHealthy() {
    try {
      if (!this.microphoneStream || !this.microphoneStream.active) {
        console.log('🚨 Stream health check: No active stream');
        return false;
      }
      
      const tracks = this.microphoneStream.getAudioTracks();
      if (tracks.length === 0) {
        console.log('🚨 Stream health check: No audio tracks');
        return false;
      }
      
      const activeTrack = tracks[0];
      if (activeTrack.readyState !== 'live') {
        console.log(`🚨 Stream health check: Track state is ${activeTrack.readyState}`);
        return false;
      }
      
      // Test if we can still get device settings
      try {
        const settings = activeTrack.getSettings();
        if (!settings.deviceId) {
          console.log('🚨 Stream health check: No device ID in settings');
          return false;
        }
      } catch (settingsError) {
        console.log('🚨 Stream health check: Cannot get track settings:', settingsError);
        return false;
      }
      
      return true;
    } catch (error) {
      console.log('🚨 Stream health check failed:', error);
      return false;
    }
  }
  
  async cleanupFailedStream() {
    try {
      if (this.microphoneStream) {
        const tracks = this.microphoneStream.getAudioTracks();
        tracks.forEach(track => {
          try {
            track.stop();
          } catch (e) {
            console.log('Error stopping track during cleanup:', e);
          }
        });
        this.microphoneStream = null;
      }
      
      // Reset audio context if it's in a bad state
      if (this.audioContext && this.audioContext.state === 'closed') {
        this.audioContext = null;
        this.createAudioContext();
      }
      
      console.log('🧹 Failed stream cleaned up successfully');
    } catch (error) {
      console.error('Error during stream cleanup:', error);
    }
  }
  
  // ============================================================================
  // ENHANCED DEVICE SWITCHING WITH RETRY MECHANISMS
  // ============================================================================
  
  async switchToSpecificDeviceWithRetry(deviceId, deviceLabel, retryCount = 0) {
    try {
      const success = await this.switchToSpecificDevice(deviceId, deviceLabel);
      if (success) {
        this.deviceSwitchRetryCount = 0; // Reset retry counter on success
        return true;
      }
      
      // If failed and we have retries left
      if (retryCount < this.maxRetryAttempts) {
        console.log(`🔄 Device switch failed, retrying... (${retryCount + 1}/${this.maxRetryAttempts})`);
        
        // Clean up failed stream before retry
        await this.cleanupFailedStream();
        
        // Wait before retry with exponential backoff
        const delay = Math.min(200 * Math.pow(2, retryCount), 1000);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        return await this.switchToSpecificDeviceWithRetry(deviceId, deviceLabel, retryCount + 1);
      }
      
      // All retries failed - attempt fallback to system default
      console.log(`🚨 All retries failed for ${deviceLabel}, attempting fallback`);
      return await this.handleDeviceDisconnectionFallback();
      
    } catch (error) {
      console.error(`Error during device switch retry: ${error}`);
      
      if (retryCount < this.maxRetryAttempts) {
        console.log(`🔄 Retrying due to error... (${retryCount + 1}/${this.maxRetryAttempts})`);
        await this.cleanupFailedStream();
        const delay = Math.min(200 * Math.pow(2, retryCount), 1000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return await this.switchToSpecificDeviceWithRetry(deviceId, deviceLabel, retryCount + 1);
      }
      
      return await this.handleDeviceDisconnectionFallback();
    }
  }
  
  async switchToSystemDefaultWithRetry(retryCount = 0) {
    try {
      const success = await this.switchToSystemDefault();
      if (success) {
        this.deviceSwitchRetryCount = 0;
        return true;
      }
      
      if (retryCount < this.maxRetryAttempts) {
        console.log(`🔄 System default switch failed, retrying... (${retryCount + 1}/${this.maxRetryAttempts})`);
        await this.cleanupFailedStream();
        const delay = Math.min(200 * Math.pow(2, retryCount), 1000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return await this.switchToSystemDefaultWithRetry(retryCount + 1);
      }
      
      console.error('🚨 Failed to switch to system default after all retries');
      return false;
      
    } catch (error) {
      console.error(`Error during system default switch: ${error}`);
      
      if (retryCount < this.maxRetryAttempts) {
        await this.cleanupFailedStream();
        const delay = Math.min(200 * Math.pow(2, retryCount), 1000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return await this.switchToSystemDefaultWithRetry(retryCount + 1);
      }
      
      return false;
    }
  }
  
  async handleDeviceDisconnectionFallback() {
    console.log('🔌 Handling device disconnection - attempting fallback strategy');
    
    try {
      // Clean up current failed stream first
      await this.cleanupFailedStream();
      
      // Strategy 1: Try system default through DeviceManager
      console.log('🔄 Fallback Step 1: Trying system default through DeviceManager...');
      await this.deviceManager.setInputDevice('default');
      this.microphoneStream = this.deviceManager.getAudioStream();
      
      if (this.microphoneStream && this.microphoneStream.active) {
        console.log('✅ Fallback successful: Switched to system default via DeviceManager');
        // Reconnect to audio processing
        await this.connectToAudioProcessing();
        return true;
      }
      
      // Strategy 2: Try any available microphone device through DeviceManager
      console.log('🔄 Fallback Step 2: Trying any available device through DeviceManager...');
      const audioInputs = this.deviceManager.getAvailableInputDevices().filter(device => 
        device.deviceId !== 'communications' &&
        device.deviceId !== this.currentDeviceId
      );
      
      for (const device of audioInputs) {
        console.log(`🔄 Trying fallback device: ${device.label}`);
        try {
          await this.deviceManager.setInputDevice(device.deviceId);
          this.microphoneStream = this.deviceManager.getAudioStream();
          
          if (this.microphoneStream && this.microphoneStream.active) {
            console.log(`✅ Fallback successful: Switched to ${device.label} via DeviceManager`);
            // Reconnect to audio processing
            await this.connectToAudioProcessing();
            return true;
          }
        } catch (error) {
          console.log(`⚠️ Failed to switch to ${device.label}:`, error.message);
        }
      }
      
      // Strategy 3: Force re-initialize with basic constraints through DeviceManager
      console.log('🔄 Fallback Step 3: Force re-initialization through DeviceManager...');
      await this.deviceManager.initialize();
      this.microphoneStream = this.deviceManager.getAudioStream();
      
      if (this.microphoneStream && this.microphoneStream.active) {
        console.log('✅ Fallback successful: Basic audio initialized via DeviceManager');
        // Reconnect to audio processing
        await this.connectToAudioProcessing();
        return true;
      }
      
      console.error('🚨 All fallback strategies failed');
      return false;
      
    } catch (error) {
      console.error('Error during disconnection fallback:', error);
      return false;
    }
  }
  
  async initializeWithBasicConstraints() {
    try {
      // Use DeviceManager for basic constraints initialization
      await this.deviceManager.initialize();
      this.microphoneStream = this.deviceManager.getAudioStream();
      
      if (this.microphoneStream && this.microphoneStream.active) {
        this.currentDeviceId = 'default';
        
        // Reconnect to audio processing
        await this.connectToAudioProcessing();
        
        console.log('✅ Basic audio constraints initialization successful via DeviceManager');
        this.notifyDeviceSwitch('default', 'System Default (Fallback)');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Basic constraints initialization failed:', error);
      return false;
    }
  }
  
  notifyDeviceSwitch(deviceId, deviceLabel) {
    // Emit device switch event
    this.emit('deviceSwitched', {
      oldDevice: 'Previous Device',
      newDevice: deviceLabel,
      oldDeviceId: this.currentDeviceId,
      newDeviceId: deviceId,
      timestamp: Date.now()
    });
    
    // Also emit as window event for cross-component communication
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('deviceSwitched', {
        detail: {
          oldDevice: 'Previous Device',
          newDevice: deviceLabel,
          oldDeviceId: this.currentDeviceId,
          newDeviceId: deviceId,
          timestamp: Date.now()
        }
      }));
    }
  }
  
  async connectToAudioProcessing() {
    try {
      if (!this.audioContext || !this.microphoneStream) {
        console.error('🚨 Cannot connect audio processing: Missing audioContext or stream');
        return false;
      }
      
      // Create analyzer if not exists
      if (!this.analyser) {
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        this.analyser.smoothingTimeConstant = 0.8;
        
        // Setup data arrays
        this.voiceData.frequency = new Float32Array(this.analyser.frequencyBinCount);
        this.voiceData.waveform = new Float32Array(this.analyser.fftSize);
      }
      
      // Create new audio source and connect
      const source = this.audioContext.createMediaStreamSource(this.microphoneStream);
      source.connect(this.analyser);
      
      // Store source reference
      this.audioSource = source;
      
      console.log('✅ Audio processing connected successfully');
      return true;
      
    } catch (error) {
      console.error('❌ Failed to connect audio processing:', error);
      return false;
    }
  }
  
  // Initialize persistent voice processing
  async initialize() {
    if (this.isInitialized) {
      // If already initialized, just ensure persistent processing is running
      if (!this.isPersistentMode && this.backgroundConfig.enabled) {
        this.startPersistentProcessing();
      }
      return true;
    }
    
    try {
      const success = await this.preloadMicrophoneAccess();
      if (success) {
        await this.setupPersistentProcessing();
        // Auto-start persistent processing on initialization
        this.startPersistentProcessing();
        this.emit('statusChange', 'ready');
      }
      return success;
    } catch (error) {
      console.error('Failed to initialize GlobalAudioManager:', error);
      this.emit('statusChange', 'error');
      return false;
    }
  }
  
  async setupPersistentProcessing() {
    if (!this.microphoneStream || !this.audioContext) {
      throw new Error('Audio stream or context not available');
    }
    
    try {
      // Create analyser for real-time audio analysis
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;
      this.analyser.smoothingTimeConstant = 0.8;
      
      // Connect microphone to analyser
      const source = this.audioContext.createMediaStreamSource(this.microphoneStream);
      source.connect(this.analyser);
      
      // Store source reference for potential reconnection
      this.audioSource = source;
      
      // Setup data arrays
      this.voiceData.frequency = new Float32Array(this.analyser.frequencyBinCount);
      this.voiceData.waveform = new Float32Array(this.analyser.fftSize);
      
      this.backgroundConfig.enabled = true;
      console.log('✅ Persistent voice processing setup complete');
      
    } catch (error) {
      console.error('❌ Failed to setup persistent processing:', error);
      throw error;
    }
  }
  
  startPersistentProcessing() {
    if (this.isPersistentMode || !this.analyser) return;
    
    this.isPersistentMode = true;
    this.emit('statusChange', 'listening');
    
    // Clear any existing interval to prevent multiple intervals ⚠️ MEMORY LEAK FIX
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
    
    // Start continuous audio processing with bound method to prevent memory leaks
    this.processingInterval = setInterval(() => {
      // Double-check if still in persistent mode before processing
      if (this.isPersistentMode && this.analyser) {
        this.processAudioData();
      }
    }, this.backgroundConfig.updateInterval);
    
    console.log('🔄 Started persistent processing with interval ID:', this.processingInterval);
  }
  
  stopPersistentProcessing() {
    if (!this.isPersistentMode) return;
    
    this.isPersistentMode = false;
    
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
    
    this.emit('statusChange', 'idle');
  }
  
  processAudioData() {
    if (!this.analyser || !this.isPersistentMode) return;
    
    try {
      // Get frequency data
      this.analyser.getFloatFrequencyData(this.voiceData.frequency);
      
      // Get waveform data
      this.analyser.getFloatTimeDomainData(this.voiceData.waveform);
      
      // Calculate volume level
      let sum = 0;
      for (let i = 0; i < this.voiceData.waveform.length; i++) {
        sum += this.voiceData.waveform[i] * this.voiceData.waveform[i];
      }
      this.voiceData.volume = Math.sqrt(sum / this.voiceData.waveform.length);
      
      // Determine if voice is active
      const wasActive = this.voiceData.isActive;
      this.voiceData.isActive = this.voiceData.volume > this.backgroundConfig.volumeThreshold;
      
      // Emit voice data to all listeners
      this.emit('voiceData', {
        frequency: new Float32Array(this.voiceData.frequency),
        waveform: new Float32Array(this.voiceData.waveform),
        volume: this.voiceData.volume,
        isActive: this.voiceData.isActive,
        timestamp: Date.now()
      });
      
      // Emit activity change events
      if (wasActive !== this.voiceData.isActive) {
        this.emit('voiceActivity', this.voiceData.isActive);
      }
      
    } catch (error) {
      console.error('Error processing audio data:', error);
    }
  }

  async createAudioContext() {
    // ⚠️ AUDIOCONTEXT LEAK FIX: Reuse existing context if available and running
    if (this.audioContext && this.audioContext.state !== 'closed') {
      // Resume if suspended, but don't create new context
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
          console.log('🔄 Resumed existing AudioContext');
        } catch (error) {
          console.warn('Failed to resume AudioContext:', error);
        }
      }
      return this.audioContext;
    }
    
    try {
      // ⚠️ AUDIOCONTEXT LEAK FIX: Close existing context properly before creating new one
      if (this.audioContext && this.audioContext.state !== 'closed') {
        console.log('🔄 Closing existing AudioContext before creating new one');
        try {
          await this.audioContext.close();
        } catch (error) {
          console.warn('Failed to close existing AudioContext:', error);
        }
      }
      
      console.log('🎧 Creating new AudioContext for continuous background listening');
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 44100,
        latencyHint: 'interactive'
      });
      
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      console.log(`✅ AudioContext created with state: ${this.audioContext.state}`);
      return this.audioContext;
    } catch (error) {
      console.error('❌ Failed to create AudioContext:', error);
      return null;
    }
  }

  async preloadMicrophoneAccess() {
    // Prevent multiple simultaneous initialization attempts
    if (this.isInitializing) {
      return this.initPromise;
    }
    
    if (this.isInitialized && this.microphoneStream && this.microphoneStream.active) {
      return this.microphoneStream;
    }

    this.isInitializing = true;
    this.setupCount++;
    
    this.initPromise = this._performPreload();
    
    try {
      const result = await this.initPromise;
      this.isInitialized = true;
      return result;
    } catch (error) {
      console.error('❌ Failed to preload microphone access:', error);
      this.isInitialized = false;
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  async _performPreload() {
    try {
      // Check if we already have permission
      const permission = await navigator.permissions.query({ name: 'microphone' });
      this.permissions.microphone = permission.state;
      this.permissions.timestamp = Date.now();
      
      if (permission.state === 'granted') {
        // Use DeviceManager to get microphone access
        await this.deviceManager.initialize();
        this.microphoneStream = this.deviceManager.getAudioStream();
        
        // Create audio context if not exists
        await this.createAudioContext();
        
        return true;
      } else if (permission.state === 'prompt') {
        // Don't request permission automatically - wait for user interaction
        return false;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error in microphone preload:', error);
      return false;
    }
  }

  async getOptimizedStream(deviceId = null) {
    // ⚠️ MEDIASTREAM LEAK FIX: Reuse existing stream if compatible and preserve for background listening
    if (this.microphoneStream && this.microphoneStream.active && !deviceId) {
      console.log('🔄 Reusing existing MediaStream for continuous background listening');
      return this.microphoneStream;
    }
    
    try {
      // Use DeviceManager to get the stream
      if (deviceId) {
        await this.deviceManager.setInputDevice(deviceId);
      }
      
      this.microphoneStream = this.deviceManager.getAudioStream();
      console.log(`✅ MediaStream from DeviceManager: ${this.microphoneStream.getAudioTracks()[0]?.label}`);
      return this.microphoneStream;
    } catch (error) {
      console.error('❌ Failed to get optimized stream from DeviceManager:', error);
      
      // If specific device failed, try default
      if (deviceId && deviceId !== 'default') {
        console.log('🔄 Falling back to default device');
        return this.getOptimizedStream('default');
      }
      
      throw error;
    }
  }

  getAudioContext() {
    return this.audioContext;
  }

  isReady() {
    return this.isInitialized && 
           this.microphoneStream && 
           this.microphoneStream.active &&
           this.audioContext && 
           this.audioContext.state !== 'closed';
  }
  
  getSetupCount() {
    return this.setupCount;
  }

  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isInitializing: this.isInitializing,
      hasStream: !!this.microphoneStream,
      hasAudioContext: !!this.audioContext,
      audioContextState: this.audioContext?.state,
      permissions: this.permissions
    };
  }

  handleVisibilityChange() {
    // Continue voice processing even when page is hidden (minimized/tray/app switch)
    // This ensures visualization state is preserved during any focus changes
    if (document.hidden) {
      // Page hidden (minimized/tray/app switch) - continue processing at reduced frequency
      if (this.processingInterval && this.isPersistentMode) {
        clearInterval(this.processingInterval);
        this.processingInterval = null; // ⚠️ MEMORY LEAK FIX: Null the reference
        this.processingInterval = setInterval(() => {
          if (this.isPersistentMode && this.analyser) {
            this.processAudioData();
          }
        }, this.backgroundConfig.updateInterval * 2);
      }
      this.emit('visibilityChange', 'hidden');
    } else {
      // Page visible - resume normal processing frequency
      if (this.processingInterval && this.isPersistentMode) {
        clearInterval(this.processingInterval);
        this.processingInterval = null; // ⚠️ MEMORY LEAK FIX: Null the reference
        this.processingInterval = setInterval(() => {
          if (this.isPersistentMode && this.analyser) {
            this.processAudioData();
          }
        }, this.backgroundConfig.updateInterval);
      }
      // Resume audio context if suspended
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      this.emit('visibilityChange', 'visible');
    }
  }

  cleanup() {
    console.log('🧹 Starting GlobalAudioManager cleanup - preserving background listening capability');
    
    // Stop persistent processing
    this.stopPersistentProcessing();
    
    // Clear stream health monitoring
    if (this.streamHealthCheckInterval) {
      clearInterval(this.streamHealthCheckInterval);
      this.streamHealthCheckInterval = null;
    }
    
    // Clear event listeners
    this.eventListeners.clear();
    
    // Remove device change listener
    if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.handleDeviceChange.bind(this));
    }
    
    // ⚠️ MEDIASTREAM LEAK FIX: Properly stop microphone stream
    if (this.microphoneStream) {
      console.log('🛑 Stopping microphone stream tracks');
      this.microphoneStream.getTracks().forEach(track => {
        track.stop();
        console.log(`🛑 Stopped track: ${track.label}`);
      });
      this.microphoneStream = null;
    }
    
    // ⚠️ AUDIOCONTEXT LEAK FIX: Properly close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      console.log(`🛑 Closing AudioContext (current state: ${this.audioContext.state})`);
      this.audioContext.close().then(() => {
        console.log('✅ AudioContext closed successfully');
      }).catch(error => {
        console.warn('Failed to close AudioContext:', error);
      });
      this.audioContext = null;
    }
    
    // ⚠️ GLOBAL EVENT LISTENER FIX: Remove all global listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('blur', this.handleBlur);
    window.removeEventListener('focus', this.handleFocus);
    
    // Clear audio source reference
    this.audioSource = null;
    this.analyser = null;
    this.isInitialized = false;
    this.isInitializing = false;
    this.setupCount = 0;
    
    // Reset device change tracking
    this.currentDeviceId = 'default';
    this.isHandlingDeviceChange = false;
    
    console.log('✅ GlobalAudioManager cleanup complete - ready for background listening restart');
  }
  
  async switchToSystemDefault() {
    try {
      console.log('🔄 Switching to new system default device through DeviceManager...');
      
      // Check if current stream is healthy first
      const currentStreamHealthy = await this.isCurrentStreamHealthy();
      if (!currentStreamHealthy) {
        console.log('🚨 Current stream is unhealthy, forcing new stream creation');
      }
      
      // Get current track info before switching (if available and healthy)
      let oldLabel = 'Unknown';
      let oldDeviceId = 'unknown';
      if (this.microphoneStream && this.microphoneStream.active && currentStreamHealthy) {
        const oldTrack = this.microphoneStream.getAudioTracks()[0];
        oldLabel = oldTrack?.label || 'Unknown';
        oldDeviceId = oldTrack?.getSettings().deviceId || 'unknown';
      }
      
      // Use DeviceManager to switch to system default
      await this.deviceManager.setInputDevice('default');
      this.microphoneStream = this.deviceManager.getAudioStream();
      
      // Get new track info
      const newTrack = this.microphoneStream.getAudioTracks()[0];
      const newLabel = newTrack?.label || 'Unknown';
      const newDeviceId = newTrack?.getSettings().deviceId || 'unknown';
      
      // Check if device actually changed by comparing both label AND deviceId, or if stream was unhealthy
      const deviceChanged = oldLabel !== newLabel || oldDeviceId !== newDeviceId || !currentStreamHealthy;
      
      if (deviceChanged) {
        console.log(`🔄 Device changed: "${oldLabel}" (${oldDeviceId}) → "${newLabel}" (${newDeviceId})`);
        
        // Update current device tracking
        this.currentDeviceId = 'default';
        
        // Reconnect audio processing
        if (this.isPersistentMode && this.analyser) {
          await this.reconnectAudioProcessing();
        }
        
        // Emit device switch event
        this.emit('deviceSwitched', {
          oldDevice: oldLabel,
          newDevice: newLabel,
          oldDeviceId: oldDeviceId,
          newDeviceId: newDeviceId,
          timestamp: Date.now()
        });
        
        // Also emit as window event for cross-component communication
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('deviceSwitched', {
            detail: {
              oldDevice: oldLabel,
              newDevice: newLabel,
              oldDeviceId: oldDeviceId,
              newDeviceId: newDeviceId,
              timestamp: Date.now()
            }
          }));
        }
        
        console.log(`✅ Automatically switched to: ${newLabel}`);
        return true; // Indicate that switch occurred
        
      } else {
        console.log(`📱 System default device unchanged: "${oldLabel}" (${oldDeviceId})`);
        return false; // Indicate no switch occurred
      }
      
    } catch (error) {
      console.error('❌ Failed to switch to system default:', error);
      return false;
    }
  }
  
  async switchToSpecificDevice(deviceId, deviceLabel) {
    try {
      console.log(`🔄 Switching to specific device through DeviceManager: ${deviceLabel} (${deviceId})`);
      
      // Check if current stream is healthy first
      const currentStreamHealthy = await this.isCurrentStreamHealthy();
      if (!currentStreamHealthy) {
        console.log('🚨 Current stream is unhealthy, proceeding with device switch');
      }
      
      // Get current track info before switching (if available and healthy)
      let oldLabel = 'Unknown';
      let oldDeviceId = 'unknown';
      if (this.microphoneStream && this.microphoneStream.active && currentStreamHealthy) {
        const oldTrack = this.microphoneStream.getAudioTracks()[0];
        oldLabel = oldTrack?.label || 'Unknown';
        oldDeviceId = oldTrack?.getSettings().deviceId || 'unknown';
      }
      
      // Use DeviceManager to switch to specific device
      await this.deviceManager.setInputDevice(deviceId);
      this.microphoneStream = this.deviceManager.getAudioStream();
      
      // Get new track info
      const newTrack = this.microphoneStream.getAudioTracks()[0];
      const newLabel = newTrack?.label || 'Unknown';
      const newDeviceId = newTrack?.getSettings().deviceId || 'unknown';
      
      console.log(`🔄 Switching from "${oldLabel}" to "${newLabel}"`);
      
      // Update current device tracking  
      this.currentDeviceId = deviceId;
      
      // Reconnect audio processing
      if (this.isPersistentMode && this.analyser) {
        await this.reconnectAudioProcessing();
      }
      
      // Emit device switch event
      this.emit('deviceSwitched', {
        oldDevice: oldLabel,
        newDevice: newLabel,
        oldDeviceId: oldDeviceId,
        newDeviceId: newDeviceId,
        timestamp: Date.now()
      });
      
      // Also emit as window event for cross-component communication
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('deviceSwitched', {
          detail: {
            oldDevice: oldLabel,
            newDevice: newLabel,
            oldDeviceId: oldDeviceId,
            newDeviceId: newDeviceId,
            timestamp: Date.now()
          }
        }));
      }
      
      console.log(`✅ Successfully switched to: ${newLabel}`);
      return true;
      
    } catch (error) {
      console.error(`❌ Failed to switch to specific device ${deviceLabel}:`, error);
      return false;
    }
  }
}

// Create global instance
export const globalAudioManager = new GlobalAudioManager();

// Make it accessible from window for cross-component communication
if (typeof window !== 'undefined') {
  window.globalAudioManager = globalAudioManager;
}

// Auto-start preloading when module loads (only if permission is already granted)
setTimeout(async () => {
  try {
    const permission = await navigator.permissions.query({ name: 'microphone' });
    if (permission.state === 'granted') {
      globalAudioManager.preloadMicrophoneAccess();
    }
  } catch (error) {
    // Permission query failed, skip auto-preload
  }
}, 100);